import React, { useState, useEffect } from 'react';
import { Trash2, Edit3, GripVertical, Check, X } from 'lucide-react';
import SimpleTimePicker from './SimpleTimePicker';

const ScheduleTable = ({
  courses,
  schedule,
  weekdays,
  timeSlots,
  onDrop,
  onManualInput,
  onDragUpdate,
  onDeleteTime,
  onDeleteDay,
  onUpdateTime,
  onAddNewCourse,
  backgroundImage,
  backgroundOpacity,
  backgroundBlur,
  backgroundMode,
  backgroundColor,
  showPeriodColumn
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('课程表（点击编辑）');
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, timeIndex: -1, dayIndex: -1, type: '' });
  const [editingCell, setEditingCell] = useState({ timeIndex: -1, dayIndex: -1, value: '' });
  const [draggedCell, setDraggedCell] = useState({ timeIndex: -1, dayIndex: -1, courseName: '' });
  const [editingTime, setEditingTime] = useState({ timeIndex: -1, value: '' });

  // 生成节次名称
  const getPeriodName = (index) => {
    const periods = ['第一节', '第二节', '第三节', '第四节', '第五节', '第六节', '第七节', '第八节', '第九节', '第十节'];
    return periods[index] || `第${index + 1}节`;
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setEditTitle('课程表（点击编辑）');
  };

  const handleTitleSubmit = () => {
    if (editTitle.trim()) {
      // 这里可以添加保存课程表名称的逻辑
      console.log('课程表名称已修改为:', editTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setEditTitle('课程表（可编辑）');
    setIsEditingTitle(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      handleTitleCancel();
    }
  };

  const handleTimeRightClick = (e, timeIndex) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      timeIndex: timeIndex,
      dayIndex: -1,
      type: 'time'
    });
  };

  const handleDayRightClick = (e, dayIndex) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      timeIndex: -1,
      dayIndex: dayIndex,
      type: 'day'
    });
  };

  const handleDeleteTime = () => {
    if (contextMenu.timeIndex >= 0 && onDeleteTime) {
      onDeleteTime(contextMenu.timeIndex);
    }
    setContextMenu({ show: false, x: 0, y: 0, timeIndex: -1, dayIndex: -1, type: '' });
  };

  const handleDeleteDay = () => {
    if (contextMenu.dayIndex >= 0 && onDeleteDay) {
      onDeleteDay(contextMenu.dayIndex);
    }
    setContextMenu({ show: false, x: 0, y: 0, timeIndex: -1, dayIndex: -1, type: '' });
  };

  // 课程编辑功能
  const handleCourseRightClick = (e, timeIndex, dayIndex, courseName) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      timeIndex,
      dayIndex,
      type: 'course',
      courseName
    });
  };

  const handleEditCourse = () => {
    if (contextMenu.timeIndex >= 0 && contextMenu.dayIndex >= 0) {
      const currentCourse = schedule[contextMenu.timeIndex] && schedule[contextMenu.timeIndex][contextMenu.dayIndex];
      setEditingCell({
        timeIndex: contextMenu.timeIndex,
        dayIndex: contextMenu.dayIndex,
        value: currentCourse || ''
      });
    }
    setContextMenu({ show: false, x: 0, y: 0, timeIndex: -1, dayIndex: -1, type: '' });
  };

  const handleDeleteCourse = () => {
    if (contextMenu.timeIndex >= 0 && contextMenu.dayIndex >= 0) {
      onManualInput('', contextMenu.timeIndex, contextMenu.dayIndex);
    }
    setContextMenu({ show: false, x: 0, y: 0, timeIndex: -1, dayIndex: -1, type: '' });
  };

  const handleEmptyCellClick = (timeIndex, dayIndex) => {
    setEditingCell({
      timeIndex: timeIndex,
      dayIndex: dayIndex,
      value: ''
    });
  };

  const handleSaveCourseEdit = () => {
    if (editingCell.timeIndex >= 0 && editingCell.dayIndex >= 0) {
      const courseName = editingCell.value.trim();
      
      if (courseName) {
        // 检查是否已存在同名课程
        const existingCourse = courses.find(c => c.name === courseName);
        
        if (existingCourse) {
          // 如果课程已存在，直接使用现有课程数据
          onManualInput(courseName, editingCell.timeIndex, editingCell.dayIndex);
        } else {
          // 如果课程不存在，提示用户是否添加新课程
          if (window.confirm(`课程"${courseName}"不存在，是否添加这个新课程？`)) {
            // 添加新课程到课程列表
            onAddNewCourse(courseName);
            // 同时添加到课程表
            onManualInput(courseName, editingCell.timeIndex, editingCell.dayIndex);
          } else {
            // 用户取消，不保存
            setEditingCell({ timeIndex: -1, dayIndex: -1, value: '' });
            return;
          }
        }
      } else {
        // 空值，直接保存
        onManualInput(courseName, editingCell.timeIndex, editingCell.dayIndex);
      }
    }
    setEditingCell({ timeIndex: -1, dayIndex: -1, value: '' });
  };

  const handleCancelCourseEdit = () => {
    setEditingCell({ timeIndex: -1, dayIndex: -1, value: '' });
  };

  // 时间编辑功能
  const handleTimeClick = (timeIndex, timeValue) => {
    setEditingTime({ timeIndex, value: timeValue });
  };

  const handleSaveTimeEdit = () => {
    if (editingTime.timeIndex >= 0 && onUpdateTime) {
      onUpdateTime(editingTime.timeIndex, editingTime.value);
      setEditingTime({ timeIndex: -1, value: '' });
    }
  };

  const handleCancelTimeEdit = () => {
    setEditingTime({ timeIndex: -1, value: '' });
  };

  // 课程拖动功能
  const handleCourseDragStart = (e, timeIndex, dayIndex, courseName) => {
    e.stopPropagation();
    setDraggedCell({ timeIndex, dayIndex, courseName });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', courseName); // 只传递课程名称
    
    // 设置拖动时的视觉效果
    e.target.style.opacity = '0.5';
  };

  const handleCourseDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCourseDrop = (e, targetTimeIndex, targetDayIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 检查是否是从课程列表拖入的（通过检查draggedCell是否为空）
    if (draggedCell.timeIndex === -1 && draggedCell.dayIndex === -1) {
      // 这是从课程列表拖入的，使用原有的handleDrop逻辑
      handleDrop(e, targetTimeIndex, targetDayIndex);
      return;
    }
    
    // 这是课程之间的拖动 - 移动课程
    if (draggedCell.timeIndex === targetTimeIndex && draggedCell.dayIndex === targetDayIndex) {
      return; // 拖到相同位置，不做任何操作
    }

    // 获取目标位置的当前课程
    const targetCourse = schedule[targetTimeIndex] && schedule[targetTimeIndex][targetDayIndex];
    
    // 移动课程（不是复制）
    if (draggedCell.timeIndex >= 0 && draggedCell.dayIndex >= 0) {
      // 如果目标位置有课程，先将其移到原位置（交换）
      if (targetCourse) {
        onDragUpdate(draggedCell.timeIndex, draggedCell.dayIndex, targetCourse);
      } else {
        // 如果目标位置为空，直接清空原位置
        onDragUpdate(draggedCell.timeIndex, draggedCell.dayIndex, '');
      }
      
      // 将拖动的课程放到目标位置
      onDragUpdate(targetTimeIndex, targetDayIndex, draggedCell.courseName);
    }
    
    setDraggedCell({ timeIndex: -1, dayIndex: -1, courseName: '' });
  };

  const handleCourseDragEnd = (e) => {
    // 恢复透明度
    e.target.style.opacity = '1';
    setDraggedCell({ timeIndex: -1, dayIndex: -1, courseName: '' });
  };

  const handleContextMenuClose = () => {
    setContextMenu({ show: false, x: 0, y: 0, timeIndex: -1, dayIndex: -1, type: '' });
  };

  // 点击外部关闭右键菜单
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.show) {
        setContextMenu({ show: false, x: 0, y: 0, timeIndex: -1, dayIndex: -1, type: '' });
      }
    };

    if (contextMenu.show) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.show]);
  const getCourseStyle = (courseName) => {
    const course = courses.find(c => c.name === courseName);
    if (!course) {
      return {};
    }
    
    // 检查课程是否有颜色属性
    if (!course.color) {
      return {};
    }
    
    // 如果有背景图片，课程颜色需要透明度
    const backgroundColor = backgroundImage 
      ? course.color + 'E6' // 添加透明度（E6 = 90%不透明度，不是全透明）
      : course.color;
    
    return {
      backgroundColor: backgroundColor,
      color: 'white',
      fontWeight: 'bold',
      borderRadius: '4px',
      padding: '4px 8px',
      textAlign: 'center',
      fontSize: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      transition: 'all 0.3s ease'
    };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, timeIndex, dayIndex) => {
    e.preventDefault();
    onDrop(e, timeIndex, dayIndex);
  };

  const handleManualInput = (e, timeIndex, dayIndex) => {
    onManualInput(e, timeIndex, dayIndex);
  };


  return (
    <div>
      {/* 导出容器 - 包含标题和表格 */}
      <div id="exportContainer" className={`p-6 rounded-xl shadow-lg ${backgroundImage ? 'bg-transparent' : 'bg-white'}`}>
        {isEditingTitle ? (
          <div className="flex justify-center items-center gap-3 mb-5">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={handleTitleSubmit}
              className="text-3xl text-primary-600 font-bold bg-transparent border-b-2 border-primary-600 text-center focus:outline-none focus:border-yellow-500"
              placeholder="请输入课程表名称"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleTitleSubmit}
                className="text-primary-600 hover:text-green-600 transition-colors duration-200"
                title="确认"
              >
                ✓
              </button>
              <button
                onClick={handleTitleCancel}
                className="text-primary-600 hover:text-red-600 transition-colors duration-200"
                title="取消"
              >
                ✕
              </button>
            </div>
          </div>
        ) : (
          <h1 
            onClick={handleTitleClick} 
            className="text-3xl text-primary-600 mb-5 text-center font-bold cursor-pointer transition-transform duration-300 hover:scale-105 group"
            title="点击修改课程表名称"
          >
            {editTitle}
          </h1>
        )}
        
        <div className={`mb-5 w-full ${backgroundMode === 'image' && backgroundImage ? 'schedule-background' : backgroundMode === 'color' ? 'schedule-background-color' : ''}`} style={{
          '--bg-opacity': backgroundOpacity,
          '--bg-blur': backgroundBlur,
          '--bg-image': backgroundMode === 'image' && backgroundImage ? `url(${backgroundImage})` : 'none',
          '--bg-color': backgroundMode === 'color' ? backgroundColor : 'transparent',
        }}>
        <table 
          id="scheduleTable" 
          className="schedule-table w-full table-fixed"
          style={{
            tableLayout: 'fixed'
          }}
        >
          <thead>
            <tr>
              <th className="relative bg-green-500 text-white align-middle" style={{ width: showPeriodColumn ? '12%' : '15%' }}>
                <div className="relative w-full h-full min-h-[60px] flex items-center justify-center">
                  {/* 对角线 - 使用SVG */}
                  <svg 
                    className="absolute inset-0 w-full h-full" 
                    viewBox="0 0 100 100" 
                    preserveAspectRatio="none"
                  >
                    <line 
                      x1="0" 
                      y1="0" 
                      x2="100" 
                      y2="100" 
                      stroke="white" 
                      strokeWidth="2"
                    />
                  </svg>
                  {/* 星期文字 - 右上角 */}
                  <div className="absolute top-2 right-2 text-xs font-medium z-10">
                    星期
                  </div>
                  {/* 时间文字 - 左下角 */}
                  <div className="absolute bottom-2 left-2 text-xs font-medium z-10">
                    时间
                  </div>
                </div>
              </th>
              {showPeriodColumn && (
                <th className="bg-gray-600 text-white align-middle" style={{ width: '8%' }}>
                  节次
                </th>
              )}
              {weekdays.map((day, index) => (
                <th 
                  key={index}
                  className="cursor-pointer hover:bg-red-50 transition-colors duration-200 align-middle"
                  style={{ width: `${(100 - (showPeriodColumn ? 20 : 15)) / weekdays.length}%` }}
                  onContextMenu={(e) => handleDayRightClick(e, index)}
                  title="右键删除此星期"
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((time, timeIndex) => (
              <tr key={timeIndex}>
                <td 
                  className="time-cell cursor-pointer hover:bg-red-50 transition-colors duration-200 align-middle"
                  style={{ width: showPeriodColumn ? '12%' : '15%' }}
                  onContextMenu={(e) => handleTimeRightClick(e, timeIndex)}
                  onClick={() => handleTimeClick(timeIndex, time)}
                  title="左键编辑，右键删除此时间"
                >
                  {editingTime.timeIndex === timeIndex ? (
                    <div className="flex items-center gap-2">
                      <SimpleTimePicker
                        value={editingTime.value}
                        onChange={(value) => setEditingTime({...editingTime, value})}
                        placeholder="选择时间"
                        className="flex-1"
                        size="small"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveTimeEdit();
                        }}
                        className="text-green-600 hover:text-green-800 p-1"
                        title="保存"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelTimeEdit();
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="取消"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    time
                  )}
                </td>
                {showPeriodColumn && (
                  <td 
                    className="text-center align-middle font-medium text-gray-700 bg-gray-50"
                    style={{ width: '8%' }}
                  >
                    {getPeriodName(timeIndex)}
                  </td>
                )}
                {weekdays.map((day, dayIndex) => {
                  const courseName = schedule[timeIndex] && schedule[timeIndex][dayIndex] ? schedule[timeIndex][dayIndex] : '';
                  const isEditing = editingCell.timeIndex === timeIndex && editingCell.dayIndex === dayIndex;
                  
                  return (
                    <td
                      key={dayIndex}
                      className={`course-cell relative group align-middle ${courseName ? 'cursor-move' : 'cursor-pointer'}`}
                      style={{ 
                        width: `${(100 - (showPeriodColumn ? 20 : 15)) / weekdays.length}%`, // 与星期列保持相同宽度
                        ...getCourseStyle(courseName) // 合并课程样式
                      }}
                      onClick={() => !courseName && !isEditing && handleEmptyCellClick(timeIndex, dayIndex)}
                      onDragOver={(e) => {
                        handleDragOver(e);
                        if (courseName) handleCourseDragOver(e);
                      }}
                      onDrop={(e) => {
                        // 总是先尝试课程间拖动，如果draggedCell为空则回退到从课程列表拖入
                        handleCourseDrop(e, timeIndex, dayIndex);
                      }}
                      onContextMenu={(e) => courseName ? handleCourseRightClick(e, timeIndex, dayIndex, courseName) : null}
                      draggable={!!courseName}
                      onDragStart={(e) => courseName ? handleCourseDragStart(e, timeIndex, dayIndex, courseName) : null}
                      onDragEnd={handleCourseDragEnd}
                      title={courseName ? "左键拖动移动，右键编辑" : "点击输入课程"}
                    >
                      {isEditing ? (
                        <div className="flex-1 text-center relative">
                          <div 
                            ref={(el) => {
                              if (el && editingCell.timeIndex >= 0 && editingCell.dayIndex >= 0) {
                                // 自动聚焦到编辑元素
                                setTimeout(() => {
                                  el.focus();
                                  // 将光标移到文本末尾
                                  const range = document.createRange();
                                  const sel = window.getSelection();
                                  range.selectNodeContents(el);
                                  range.collapse(false);
                                  sel.removeAllRanges();
                                  sel.addRange(range);
                                }, 0);
                              }
                            }}
                            className="cursor-text"
                            contentEditable
                            suppressContentEditableWarning={true}
                            onInput={(e) => {
                              // 如果正在使用中文输入法，不更新状态
                              if (e.target.getAttribute('data-composing') === 'true') {
                                return;
                              }
                              // 使用innerText而不是textContent来避免中文输入法问题
                              const value = e.target.innerText || e.target.textContent || '';
                              setEditingCell({...editingCell, value: value});
                            }}
                            onCompositionStart={(e) => {
                              // 中文输入法开始
                              e.target.setAttribute('data-composing', 'true');
                            }}
                            onCompositionEnd={(e) => {
                              // 中文输入法结束
                              e.target.removeAttribute('data-composing');
                              const value = e.target.innerText || e.target.textContent || '';
                              setEditingCell({...editingCell, value: value});
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveCourseEdit();
                              } else if (e.key === 'Escape') {
                                handleCancelCourseEdit();
                              }
                            }}
                            onBlur={handleSaveCourseEdit}
                            style={{ minHeight: '20px', outline: 'none' }}
                          >
                            {editingCell.value}
                          </div>
                          {/* 编辑提示 */}
                          <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 bg-white px-2 py-1 rounded shadow-sm border whitespace-nowrap z-10">
                            回车保存，Esc取消
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 text-center">
                          <div className="font-semibold text-base">{courseName}</div>
                          {(() => {
                            const course = courses.find(c => c.name === courseName);
                            if (course && (course.teacher || course.location)) {
                              return (
                                <div className="text-xs opacity-90 mt-1">
                                  {course.teacher && <div>教师: {course.teacher}</div>}
                                  {course.location && <div>地点: {course.location}</div>}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      
      {/* 右键菜单 */}
      {contextMenu.show && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[120px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.type === 'time' && (
            <button
              className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors duration-200"
              onClick={handleDeleteTime}
            >
              <Trash2 size={16} />
              删除时间
            </button>
          )}
          {contextMenu.type === 'day' && (
            <button
              className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors duration-200"
              onClick={handleDeleteDay}
            >
              <Trash2 size={16} />
              删除星期
            </button>
          )}
          {contextMenu.type === 'course' && (
            <>
              <button
                className="w-full px-4 py-2 text-left text-blue-600 hover:bg-blue-50 flex items-center gap-2 transition-colors duration-200"
                onClick={handleEditCourse}
              >
                <Edit3 size={16} />
                编辑课程
              </button>
              <button
                className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors duration-200"
                onClick={handleDeleteCourse}
              >
                <Trash2 size={16} />
                删除课程
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleTable;
